import * as React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Typography, Accordion, AccordionSummary, AccordionDetails, Box, Grid } from '@mui/material';
import { useSelector } from 'react-redux';

import styles from './ProductOrderSummary.module.scss';
import { DynamicComponentProps } from '@/types/ModuleComponentProp.type';
import { useTranslation } from 'react-i18next';
import { getProductDisplayText } from '@/utils/inspection.utils';
import { RootState } from '@/store/rootReducer';
import DynamicSVG from '@/components/DynamicSvg';
import { APP_CONFIG_KEY } from '@/types/AppConfig.type';

export const ProductOrderSummary: React.FC<DynamicComponentProps> = ({ inspection, structure }: DynamicComponentProps) => {
    const { t: translate } = useTranslation();
    const config = useSelector((state: RootState) => state.appConfig);
    
    const showExternalPOFilter = config.filters?.some(filter => filter === 'externalPO');
    const productText = (config?.[APP_CONFIG_KEY.useText] as string) ?? 'Product';
    
    // if (!inspection) return null;

    return (
        <div className={styles.details}>
            <Accordion defaultExpanded sx={{
                boxShadow: 'none',
                minHeight: '80px',
                '&.MuiAccordion-root': {
                    minHeight: '80px',
                    '&.Mui-expanded': {
                        minHeight: '80px',
                    },
                    '&::before': {
                        display: 'none'
                    }
                }
            }}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel3-content"
                    id="panel3-header"
                    sx={{
                        minHeight: '80px',      
                        height: '80px',
                        '&.Mui-expanded': {
                            minHeight: '80px',
                            height: '80px'
                        },
                        '& .MuiAccordionSummary-content': {
                            margin: '12px 0',
                            '&.Mui-expanded': {
                                margin: '12px 0'
                            }
                        }
                    }}
                >
                    <Box sx={{ 
                        display: 'flex', 
                        width: '100%', 
                        alignItems: 'center',
                        position: 'relative'
                    }}>
                        {!!structure?.icon && 
                            <Box sx={{
                                width: 90,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                borderRight: '.05rem solid #A7A7A7',
                                paddingRight: '1rem',
                                marginRight: '1rem'
                            }}>
                                <DynamicSVG iconName={structure.icon} />
                            </Box>
                        }
                        <Box sx={{ 
                            flexGrow: 2, 
                            textAlign: 'center', 
                            fontWeight: 'bold', 
                            color: '#000000B3',
                            paddingRight: '24px' // Space for expand icon
                        }}> 
                            {translate(structure.header || 'Summary')} 
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid direction='column'>
                        <Grid container justifyContent="space-between" alignItems="center" sx={{ borderTop: ".05rem solid #A7A7A7", borderBottom: ".05rem solid #ECECEB", padding: '.67rem 0' }}>
                            <Grid size={2}></Grid>
                            <Grid size={3}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: "600" }}>
                                    {translate(productText)}
                                </Typography> </Grid>
                            <Grid size={7}> <Typography variant="body1">{getProductDisplayText(inspection?.product)}</Typography></Grid>
                        </Grid>
                        <Grid container justifyContent="space-between" alignItems="center" sx={{ padding: '.67rem 0 0' }}>
                            <Grid size={2}></Grid>
                            <Grid size={3}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: "600" }}>
                                    {translate(showExternalPOFilter ? 'External Order Ref.' : 'Order Number')}
                                </Typography> </Grid>
                            <Grid size={7}> 
                                <Typography variant="body1">
                                    {showExternalPOFilter ? inspection?.externalOrderReference : inspection?.internalOrderReference}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </div>
    );
}